import { getHeroSliderSettings } from './slider-utils.mjs';

const getSliderElements = (root) => {
    const normalizedRoot = root?.jquery ? root[0] : root;

    if (!normalizedRoot?.querySelectorAll) return [];

    return [
        ...(normalizedRoot.matches?.('[data-hero-slider]') ? [normalizedRoot] : []),
        ...normalizedRoot.querySelectorAll('[data-hero-slider]'),
    ];
};

const initializeHeroSlider = (element) => {
    if (element.dataset.heroSliderReady === 'true') return;

    const slides = [...element.querySelectorAll('.swiper-slide')];
    const slideLabels = slides.map(
        (slide) => slide.dataset.heroSlideLabel || slide.getAttribute('aria-label') || '',
    );
    const controls = element.querySelector('[data-hero-controls]');
    const pagination = element.querySelector('[data-hero-pagination]');
    const autoplayToggle = element.querySelector('[data-hero-autoplay-toggle]');
    const liveRegion = element.querySelector('[data-hero-live]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const settings = getHeroSliderSettings({
        slideCount: slides.length,
        autoplay: element.dataset.autoplay,
        autoplayDelay: element.dataset.autoplayDelay,
        transitionDuration: element.dataset.transitionDuration,
        pauseOnHover: element.dataset.pauseOnHover,
        reducedMotion: reducedMotion.matches,
    });

    if (!settings.hasMultipleSlides) {
        controls?.remove();
        element.dataset.heroSliderState = 'static';
        element.dataset.heroSliderReady = 'true';
        return;
    }

    if (!window.Swiper) {
        element.dataset.heroSliderState = 'static';
        return;
    }

    const pauseReasons = new Set(settings.reducedMotion ? ['reduced-motion'] : []);
    const labels = {
        pause: element.dataset.labelPause || 'Wstrzymaj automatyczną zmianę slajdów',
        resume: element.dataset.labelResume || 'Wznów automatyczną zmianę slajdów',
        slide: element.dataset.labelSlide || 'Przejdź do slajdu',
    };
    let userPaused = false;

    const autoplayOptions = settings.autoplayRequested
        ? {
              delay: settings.autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
              waitForTransition: true,
          }
        : false;

    const announceSlide = (swiper) => {
        if (!liveRegion) return;

        const slideLabel = slideLabels[swiper.realIndex];
        const position = `${swiper.realIndex + 1} / ${slides.length}`;

        liveRegion.textContent = slideLabel ? `${position}: ${slideLabel}` : position;
    };

    const updateAutoplayToggle = () => {
        if (!autoplayToggle) return;

        autoplayToggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        autoplayToggle.setAttribute('aria-label', userPaused ? labels.resume : labels.pause);
    };

    const swiper = new window.Swiper(element, {
        effect: settings.effect,
        fadeEffect: {
            crossFade: settings.crossFade,
        },
        speed: settings.transitionDuration,
        rewind: settings.rewind,
        loop: false,
        allowTouchMove: settings.allowTouchMove,
        grabCursor: settings.allowTouchMove,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        keyboard: {
            enabled: true,
            onlyInViewport: true,
        },
        pagination: pagination
            ? {
                  el: pagination,
                  clickable: true,
              }
            : false,
        autoplay: autoplayOptions,
        a11y: {
            enabled: true,
            containerRole: 'group',
            containerRoleDescriptionMessage: 'karuzela',
            itemRoleDescriptionMessage: 'slajd',
            slideLabelMessage: '{{index}} / {{slidesLength}}',
            paginationBulletMessage: `${labels.slide} {{index}}`,
        },
        on: {
            init(instance) {
                slides.forEach((slide, index) => {
                    if (slideLabels[index]) {
                        slide.setAttribute(
                            'aria-label',
                            `${index + 1} / ${slides.length}: ${slideLabels[index]}`,
                        );
                    }
                });
                element.dataset.heroSliderState = settings.reducedMotion
                    ? 'reduced-motion'
                    : 'ready';
                announceSlide(instance);
            },
            slideChange(instance) {
                announceSlide(instance);
                element.dispatchEvent(
                    new CustomEvent('seluvia:hero-slider-change', {
                        bubbles: true,
                        detail: {
                            index: instance.realIndex,
                            slideCount: slides.length,
                        },
                    }),
                );
            },
        },
    });

    const syncAutoplay = () => {
        if (!settings.autoplayRequested || !swiper.autoplay) return;

        if (userPaused || pauseReasons.size > 0) {
            swiper.autoplay.pause();
            return;
        }

        if (!swiper.autoplay.running) {
            swiper.autoplay.start();
            return;
        }

        swiper.autoplay.resume();
    };

    const setPauseReason = (reason, shouldPause) => {
        if (!settings.autoplayRequested) return;

        if (shouldPause) {
            pauseReasons.add(reason);
        } else {
            pauseReasons.delete(reason);
        }

        syncAutoplay();
    };

    if (autoplayToggle) {
        autoplayToggle.addEventListener('click', () => {
            userPaused = !userPaused;
            updateAutoplayToggle();
            syncAutoplay();
        });
    }

    if (settings.pauseOnHover) {
        element.addEventListener('pointerenter', () => setPauseReason('pointer', true));
        element.addEventListener('pointerleave', () => setPauseReason('pointer', false));
        element.addEventListener('focusin', () => setPauseReason('focus', true));
        element.addEventListener('focusout', (event) => {
            if (!element.contains(event.relatedTarget)) setPauseReason('focus', false);
        });
    }

    document.addEventListener('visibilitychange', () => {
        setPauseReason('document-hidden', document.hidden);
    });

    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
            ([entry]) => setPauseReason('offscreen', !entry?.isIntersecting),
            { threshold: 0.2 },
        );

        observer.observe(element);
    }

    const handleReducedMotionChange = (event) => {
        swiper.params.speed = event.matches ? 0 : getHeroSliderSettings({
            slideCount: slides.length,
            transitionDuration: element.dataset.transitionDuration,
        }).transitionDuration;
        element.dataset.heroSliderState = event.matches ? 'reduced-motion' : 'ready';
        setPauseReason('reduced-motion', event.matches);
    };

    if (typeof reducedMotion.addEventListener === 'function') {
        reducedMotion.addEventListener('change', handleReducedMotionChange);
    } else {
        reducedMotion.addListener(handleReducedMotionChange);
    }

    updateAutoplayToggle();
    syncAutoplay();
    element.dataset.heroSliderReady = 'true';
};

const initializeHeroSliders = (root = document) => {
    getSliderElements(root).forEach(initializeHeroSlider);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeHeroSliders(), { once: true });
} else {
    initializeHeroSliders();
}

window.acf?.addAction('render_block_preview/type=hero-slider', initializeHeroSliders);
document.addEventListener('seluvia:block-rendered', (event) => {
    initializeHeroSliders(event.detail?.element || event.target);
});
